'use client';

import type { ClassValue } from 'clsx';
import type { ReactNode } from 'react';
import { HrefElementType } from '../../../../type/category';

import { NavigationItem } from '@vs-shop/ui/navigationList';
import { clsxm } from '@vs-shop/utils';
import { Match } from 'effect';

import { navigateFullPage } from '../../../../utils/router';

import Link from '@vs-shop/image-link/Link';

/**
 * The tenant half of a navigation entry: which element the API asked for, built
 * out of this app's router and this app's full-page navigation.
 *
 * The generic half — painting a caller-supplied element with the navigation's
 * slot and classes — is `@vs-shop/ui`'s `NavigationItem`. The enum and
 * `navigateFullPage` stay here because they are Sylius's and Next's
 * respectively, and neither belongs in a shared package.
 *
 * Where a navigation entry IS a section part — a megamenu word, a tile title, a
 * child link — the part paints itself and takes the element through its own
 * `render`, so those call sites reach for `Link` directly rather than for this.
 */
type NavElementProps = {
  hrefElementType: HrefElementType;
  href: string;
  title: string;
  children: ReactNode;
  className?: ClassValue;
};

export function NavigationItemElement({
  hrefElementType,
  href,
  title,
  className,
  children,
}: NavElementProps) {
  return (
    <NavigationItem
      className={clsxm(className)}
      render={Match.value(hrefElementType).pipe(
        // A button branch has no `href`, so it offers no middle-click, no
        // "open in new tab" and nothing for a crawler to follow. Unreachable
        // today — every call site asks for an anchor — and the barrier is
        // recorded in `packages/rcprofi/.docs/MODULARIZED.md`.
        Match.when(HrefElementType.Button, () => (
          <button
            type="button"
            title={title}
            onClick={() => {
              navigateFullPage(href);
            }}
          />
        )),
        Match.when(HrefElementType.Anchor, () => (
          <Link href={href} title={title} prefetch={false} />
        )),
        Match.exhaustive
      )}
    >
      {children}
    </NavigationItem>
  );
}
